import { useState } from 'react'
import { Navigate } from 'react-router'

import fuelAppLogo from '@/assets/marketing/fuelapp/fuelapp-logo.svg'
import truckFueling from '@/assets/marketing/fuelapp/teams-truck-fueling.jpg'
import { Anchor } from '@/components/ui/Anchor'
import { trackMarketingCta } from '@/lib/analytics'
import { useHostGuard } from '@/lib/hostGuard'
import { cn } from '@/lib/utils'
import { CodeScreen, InviteScreen, IPhone, TeamScreen } from './components/FuelAppTeamsPhones'
import { HubSpotFormEmbed } from './components/HubSpotFormEmbed'

/* Standalone FuelApp Teams landing page — own header/footer, no MarketingLayout. */

const HUBSPOT_PORTAL_ID = '246335544'
const HUBSPOT_FORM_ID = '30a1022a-1506-4342-bf49-65f11603be52'
const HUBSPOT_REGION = 'na2'

/** Marketing claims a marketer may want to change without touching layout. */
const SAVINGS_PER_GALLON = '43¢'
const TRUCK_STOP_COUNT = '1,800+'

const STATS = [
  { figure: SAVINGS_PER_GALLON, caption: 'average savings per gallon', highlight: true },
  { figure: TRUCK_STOP_COUNT, caption: 'truck stops nationwide', highlight: false },
  { figure: '$29', caption: 'savings per fill', highlight: false },
]

const STEPS = [
  {
    title: 'Set up your fleet',
    body: "Apply for an account. Once you're approved, add your business details and a team card.",
  },
  {
    title: 'Invite your drivers',
    body: "Enter each driver's phone number and set a weekly gallon limit. They'll get a text to download FuelApp and join your team.",
  },
  {
    title: 'Drivers pay (and save) with fuel codes',
    body: 'Every fill is discounted and charged to your team card.',
  },
]

const FAQS = [
  {
    question: 'What does Teams cost?',
    answer:
      "Nothing. There's no subscription and no per-driver fee. You pay for the fuel your drivers pump, at the discounted price.",
  },
  {
    question: 'Do my drivers need to do anything differently?',
    answer: 'No. They generate a code at the pump, same as today. The team card gets charged automatically.',
  },
  {
    question: 'What happens if a driver hits their weekly limit?',
    answer:
      "They'll see a message to check in with you, and you can increase their gallon limit as needed. Nobody gets stuck at the pump.",
  },
  {
    question: 'What payment methods does Teams support?',
    answer:
      "A credit or debit card, added by the admin. Each fill charges that card like a personal fill-up. There's no prepaid balance to fund and no credit line to apply for.",
  },
]

const H2 =
  'text-[clamp(32px,3.8vw,52px)] leading-[1.1] font-medium tracking-[-0.02em] text-balance text-[#101013]'
const SECTION = 'mx-auto max-w-[1200px] px-6'
// Fixed breakpoint, not auto-fit, so the image/text order swap lines up with the column switch.
const FEATURE_ROW =
  'grid grid-cols-1 items-center gap-[clamp(40px,6vw,96px)] pt-[clamp(72px,9vw,128px)] lg:grid-cols-2'

function ApplyLink({ large, cta }: { large?: boolean; cta: string }) {
  return (
    <Anchor
      href="#apply"
      onClick={() => trackMarketingCta(cta)}
      className={cn(
        'inline-flex items-center rounded-lg bg-[#FFCA42] font-semibold whitespace-nowrap text-[#101013] transition-colors hover:bg-[#F5BC1F] hover:text-[#101013]',
        large ? 'px-[30px] py-[18px] text-[19px]' : 'px-5 py-3 text-base max-[389px]:px-4'
      )}
    >
      {large ? (
        'Get FuelApp Teams'
      ) : (
        <>
          <span className="max-[389px]:hidden">Get FuelApp Teams</span>
          <span className="min-[390px]:hidden">Get Teams</span>
        </>
      )}
    </Anchor>
  )
}

function ProductPanel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex h-[clamp(400px,40vw,500px)] items-start justify-center overflow-hidden rounded-2xl bg-white pt-12',
        className
      )}
    >
      {children}
    </div>
  )
}

function FeatureCopy({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex max-w-[500px] flex-col gap-5">
      <h2 className={H2}>{title}</h2>
      <p className="text-[19px] leading-[1.55] text-pretty text-[#555962]">{children}</p>
    </div>
  )
}

function FaqItem({
  question,
  answer,
  open,
  onToggle,
}: {
  question: string
  answer: string
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="border-b border-[#C7D1DA]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between gap-6 py-6 text-left text-[clamp(18px,1.7vw,21px)] font-medium text-[#101013]"
      >
        <span>{question}</span>
        <span className="flex-none text-[28px] leading-none font-normal text-[#E0A400]">
          {open ? '−' : '+'}
        </span>
      </button>
      {open && <p className="pr-12 pb-6 text-[17px] leading-[1.6] text-pretty text-[#555962]">{answer}</p>}
    </div>
  )
}

export function FuelAppTeams() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const { redirecting, navigateTo } = useHostGuard('marketing')
  if (navigateTo) return <Navigate to={navigateTo} replace />
  if (redirecting) return null

  return (
    <div className="fuelapp-brand min-h-screen w-full shrink-0 overflow-x-clip bg-[#F4F7F9] font-sans text-[#101013] antialiased">
      <title>FuelApp Teams — spend less on fueling your fleet</title>
      <meta
        name="description"
        content="Save 43¢/gallon on average and manage every driver's fuel spend from your phone. Drivers pay with a code, not a card. Free for your fleet."
      />

      <header className="sticky top-0 z-10 border-b border-[#E1E7EC] bg-[#F4F7F9]/92 backdrop-blur-[10px]">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-6 py-3.5">
          <Anchor href="/fuelapp" className="block shrink-0">
            <img src={fuelAppLogo} alt="FuelApp" className="block h-7.5 w-auto" />
          </Anchor>
          <nav className="flex items-center gap-7">
            <Anchor
              href="#how"
              className="text-base font-medium text-[#555962] hover:text-[#101013] max-sm:hidden"
            >
              How it works
            </Anchor>
            <Anchor
              href="#faq"
              className="text-base font-medium text-[#555962] hover:text-[#101013] max-sm:hidden"
            >
              FAQ
            </Anchor>
            <ApplyLink cta="fuelapp_teams_join_header" />
          </nav>
        </div>
      </header>

      <section
        className={cn(
          SECTION,
          'grid grid-cols-[repeat(auto-fit,minmax(min(100%,460px),1fr))] items-center gap-[clamp(48px,6vw,80px)] pt-[clamp(40px,7vw,96px)] pb-[clamp(56px,8vw,112px)]'
        )}
      >
        <div className="flex flex-col gap-8">
          <h1 className="text-[clamp(36px,4.6vw,60px)] leading-[1.1] font-medium tracking-[-0.02em] text-balance">
            Spend less on fueling your fleet
          </h1>
          <p className="max-w-[520px] text-[clamp(18px,1.6vw,21px)] leading-normal text-pretty text-[#555962]">
            Save <span className="font-bold text-[#101013]">{SAVINGS_PER_GALLON}/gallon</span> on average, and
            manage every driver&apos;s fuel spend from your phone. No cards to manage.
          </p>
          <div>
            <ApplyLink large cta="fuelapp_teams_join_hero" />
          </div>
        </div>
        <div className="relative pb-14">
          <div className="relative h-[clamp(380px,46vw,560px)] overflow-hidden rounded-2xl bg-[#DDE3E8]">
            <img
              src={truckFueling}
              alt="A semi truck fueling at a diesel island"
              className="absolute inset-0 block h-full w-full object-cover"
              fetchPriority="high"
            />
          </div>
          <div className="absolute right-[clamp(-40px,-2vw,-8px)] bottom-0">
            <IPhone scale={0.52}>
              <TeamScreen />
            </IPhone>
          </div>
        </div>
      </section>

      <section className="bg-[#101013]">
        <div className={cn(SECTION, 'flex flex-col gap-7 py-[clamp(48px,6vw,72px)]')}>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-8">
            {STATS.map((stat) => (
              <div key={stat.caption} className="flex flex-col gap-1.5">
                <span
                  className={cn(
                    'text-[clamp(48px,5.4vw,68px)] font-bold tracking-[-0.02em]',
                    stat.highlight ? 'text-[#FFCA42]' : 'text-white'
                  )}
                >
                  {stat.figure}
                </span>
                <span className="text-lg text-[#C7D1DA]">{stat.caption}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-[#8B909A]">
            Volume-weighted average discount across FuelApp fills, trailing 90 days.
          </p>
        </div>
      </section>

      <section className={cn(SECTION, FEATURE_ROW)}>
        <FeatureCopy title="Set fuel limits for every driver.">
          Give each driver a weekly gallon limit. Raise it, lower it, or cut access from your phone.
        </FeatureCopy>
        <ProductPanel>
          <IPhone scale={0.62}>
            <InviteScreen />
          </IPhone>
        </ProductPanel>
      </section>

      <section className={cn(SECTION, FEATURE_ROW)}>
        <FeatureCopy
          title={
            <>
              Manage fuel spend.
              <br />
              Kill card fraud.
            </>
          }
        >
          Drivers pay with a code, not a card. Safe from skimmers, and nothing to lose or reissue.
        </FeatureCopy>
        <ProductPanel className="lg:order-first">
          <IPhone scale={0.62}>
            <CodeScreen />
          </IPhone>
        </ProductPanel>
      </section>

      <section id="how" className={cn(SECTION, 'flex flex-col gap-10 py-[clamp(80px,10vw,144px)]')}>
        <h2 className={H2}>How it works</h2>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] gap-4">
          {STEPS.map((step, i) => (
            <div key={step.title} className="flex flex-col gap-3.5 rounded-2xl bg-white p-7">
              <span className="flex size-10 items-center justify-center rounded-full bg-[#101013] text-[17px] font-semibold text-white">
                {i + 1}
              </span>
              <h3 className="text-[22px] leading-[1.25] font-medium text-[#101013]">{step.title}</h3>
              <p className="text-base leading-[1.55] text-pretty text-[#555962]">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="apply" className="bg-[#FFCA42]">
        {/* The FuelApp mobile app still links to /fuelapp/teams#beta. */}
        <span id="beta" aria-hidden="true" className="block" />
        <div
          className={cn(
            SECTION,
            'grid grid-cols-[repeat(auto-fit,minmax(min(100%,440px),1fr))] items-start gap-[clamp(40px,6vw,96px)] py-[clamp(64px,8vw,112px)]'
          )}
        >
          <div className="flex flex-col gap-5">
            <h2 className="text-[clamp(40px,5vw,68px)] leading-[1.05] font-medium tracking-[-0.02em] text-[#101013]">
              Take control of your fleet&apos;s fuel spend
            </h2>
            <p className="max-w-[460px] text-[19px] leading-[1.55] text-pretty text-[#101013]">
              FuelApp Teams is free to use. No fees. You only pay for what your team pumps.
            </p>
            <p className="max-w-[460px] text-[19px] leading-[1.55] font-semibold text-[#101013]">
              Apply for your free account today.
            </p>
          </div>
          <div className="rounded-2xl bg-white p-[clamp(24px,3vw,36px)] shadow-[0_12px_40px_rgba(16,16,19,0.10)]">
            <HubSpotFormEmbed
              portalId={HUBSPOT_PORTAL_ID}
              formId={HUBSPOT_FORM_ID}
              region={HUBSPOT_REGION}
              onSubmitted={() => trackMarketingCta('fuelapp_teams_beta_signup')}
            />
            <p className="mt-4 text-sm leading-normal text-[#555962]">
              We review every application before activating an account. We&apos;ll email you with next steps.
            </p>
          </div>
        </div>
      </section>

      <section
        id="faq"
        className="mx-auto flex max-w-[880px] flex-col gap-8 px-6 py-[clamp(80px,10vw,128px)]"
      >
        <h2 className={H2}>FAQs</h2>
        <div className="border-t border-[#C7D1DA]">
          {FAQS.map((faq, i) => (
            <FaqItem
              key={faq.question}
              question={faq.question}
              answer={faq.answer}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </section>

      <footer className="border-t border-[#E1E7EC]">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-4 px-6 py-8 text-sm text-[#555962]">
          <img src={fuelAppLogo} alt="FuelApp" className="block h-7 w-auto" />
          <div className="flex flex-wrap items-center gap-6">
            <Anchor href="/terms" className="text-[#555962] hover:text-[#101013]">
              Terms
            </Anchor>
            <Anchor href="/privacy" className="text-[#555962] hover:text-[#101013]">
              Privacy
            </Anchor>
            <span>© 2026 FuelApp by Cai Labs</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
